import React, { useEffect, useState } from "react";
import { supabase } from "../../supabase";
import AdminDashboardNav from "../comps/AdminDashboardNav";
import { FiPackage, FiSearch, FiCheckCircle, FiXCircle, FiToggleLeft, FiToggleRight, FiEdit2, FiX, FiPlus, FiTrash2 } from 'react-icons/fi';
import toast, { Toaster } from 'react-hot-toast';
import "./ProductManagement.css";
import "../styles/newCoupon.css";
import "../styles/AdminShared.css";

const EMPTY_EDIT_FORM = {
  name: "",
  description: "",
  price: "",
  category: "",
  duration: "",
  bonusMinutes: "",
  stampAmounts: "",
};

// The number inputs already have min="0", but that only blocks submission through
// the real browser UI - anything driving the form programmatically (or a future
// noValidate) would sail past it, and this field feeds checkout pricing directly.
function hasNegativeNumericField(form) {
  return ['price', 'duration', 'bonusMinutes', 'stampAmounts'].some(
    (key) => form[key] !== "" && Number(form[key]) < 0
  );
}

function toProductShape(row) {
  return {
    id: row.id, name: row.name, description: row.description, category: row.category,
    price: row.price, duration: row.duration, bonusMinutes: row.bonus_minutes,
    stampAmounts: row.stamp_amounts, type: row.type, active: row.active,
  };
}

export default function ProductManagement() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [updatingProduct, setUpdatingProduct] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_EDIT_FORM);
  const [saving, setSaving] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState(EMPTY_EDIT_FORM);
  const [adding, setAdding] = useState(false);
  const [deleteConfirmProduct, setDeleteConfirmProduct] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    async function fetchProducts() {
      try {
        const { data, error } = await supabase.from("products").select("*").order("category");
        if (error) throw error;
        setProducts((data || []).map(toProductShape));
      } catch (error) {
      console.error("Failed to fetch products:", error);
    } finally {
        setLoading(false);
      }
    }

    fetchProducts();
  }, []);

  if (loading) {
    return (
      <>
        <AdminDashboardNav />
        <div className="pm-loading">
          <div className="spinner"></div>
          <p>Laster produkter...</p>
        </div>
      </>
    );
  }

  // Group products by category
  const groupedProducts = products.reduce((groups, product) => {
    const category = product.category || "Other";
    if (!groups[category]) groups[category] = [];
    groups[category].push(product);
    return groups;
  }, {});

  // Sort products within each category by price
  Object.keys(groupedProducts).forEach(category => {
    groupedProducts[category].sort((a, b) => a.price - b.price);
  });

  // Get all categories for filter dropdown
  const allCategories = Object.keys(groupedProducts).sort();

  // Filter products based on search term, selected category, and status
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || product.category === selectedCategory;
    const matchesStatus = statusFilter === 'all' || 
                         (statusFilter === 'active' && product.active !== false) ||
                         (statusFilter === 'inactive' && product.active === false);
    return matchesSearch && matchesCategory && matchesStatus;
  });

  // Count active vs inactive products
  const activeCount = products.filter(p => p.active !== false).length;
  const inactiveCount = products.length - activeCount;

  // Toggle product active status
  const toggleProductStatus = async (productId, currentStatus) => {
    try {
      setUpdatingProduct(productId);
      // If currentStatus is false or undefined/null, set to opposite
      const isCurrentlyActive = currentStatus !== false;
      const newStatus = !isCurrentlyActive;

      const { error } = await supabase.from("products").update({ active: newStatus }).eq("id", productId);
      if (error) throw error;

      // Update local state
      setProducts(products.map(p => 
        p.id === productId ? { ...p, active: newStatus } : p
      ));
      
    } catch (error) {
      console.error("Failed to update product status:", error);
      alert(`Kunne ikke oppdatere produktstatus: ${error.message}`);
    } finally {
      setUpdatingProduct(null);
    }
  };

  const openEditModal = (product) => {
    setEditingProduct(product);
    setEditForm({
      name: product.name || "",
      description: product.description || "",
      price: product.price ?? "",
      category: product.category || "",
      duration: product.duration ?? "",
      bonusMinutes: product.bonusMinutes ?? "",
      stampAmounts: product.stampAmounts ?? "",
    });
  };

  const closeEditModal = () => {
    setEditingProduct(null);
    setEditForm(EMPTY_EDIT_FORM);
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!editingProduct) return;
    if (!editForm.name.trim() || editForm.price === "") {
      toast.error('Navn og pris er påkrevd.', { duration: 3000, position: 'top-center' });
      return;
    }
    if (hasNegativeNumericField(editForm)) {
      toast.error('Tall kan ikke være negative.', { duration: 3000, position: 'top-center' });
      return;
    }

    const updates = {
      name: editForm.name.trim(),
      description: editForm.description.trim(),
      price: Number(editForm.price) || 0,
      category: editForm.category.trim() || 'Other',
    };
    if (editForm.duration !== "") updates.duration = Number(editForm.duration) || 0;
    if (editForm.bonusMinutes !== "") updates.bonusMinutes = Number(editForm.bonusMinutes) || 0;
    if (editForm.stampAmounts !== "") updates.stampAmounts = Number(editForm.stampAmounts) || 0;

    const dbUpdates = {
      name: updates.name, description: updates.description, price: updates.price, category: updates.category,
      ...(updates.duration !== undefined ? { duration: updates.duration } : {}),
      ...(updates.bonusMinutes !== undefined ? { bonus_minutes: updates.bonusMinutes } : {}),
      ...(updates.stampAmounts !== undefined ? { stamp_amounts: updates.stampAmounts } : {}),
    };

    setSaving(true);
    try {
      const { error } = await supabase.from("products").update(dbUpdates).eq("id", editingProduct.id);
      if (error) throw error;
      setProducts((prev) => prev.map((p) => (p.id === editingProduct.id ? { ...p, ...updates } : p)));
      toast.success('Produkt oppdatert!', { duration: 2500, position: 'top-center' });
      closeEditModal();
    } catch (error) {
      console.error("Failed to update product:", error);
      toast.error(`Kunne ikke oppdatere produkt: ${error.message}`, { duration: 4000, position: 'top-center' });
    } finally {
      setSaving(false);
    }
  };

  const openAddModal = () => {
    setAddForm(EMPTY_EDIT_FORM);
    setShowAddModal(true);
  };

  const closeAddModal = () => {
    setShowAddModal(false);
    setAddForm(EMPTY_EDIT_FORM);
  };

  const handleAddFormChange = (e) => {
    const { name, value } = e.target;
    setAddForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!addForm.name.trim() || addForm.price === "" || !addForm.category.trim()) {
      toast.error('Navn, pris og kategori er påkrevd.', { duration: 3000, position: 'top-center' });
      return;
    }
    if (hasNegativeNumericField(addForm)) {
      toast.error('Tall kan ikke være negative.', { duration: 3000, position: 'top-center' });
      return;
    }

    const newProduct = {
      name: addForm.name.trim(),
      description: addForm.description.trim(),
      price: Number(addForm.price) || 0,
      category: addForm.category.trim(),
      active: true,
    };
    if (addForm.duration !== "") newProduct.duration = Number(addForm.duration) || 0;
    if (addForm.bonusMinutes !== "") newProduct.bonusMinutes = Number(addForm.bonusMinutes) || 0;
    if (addForm.stampAmounts !== "") newProduct.stampAmounts = Number(addForm.stampAmounts) || 0;

    const dbProduct = {
      name: newProduct.name, description: newProduct.description, price: newProduct.price,
      category: newProduct.category, active: true,
      ...(newProduct.duration !== undefined ? { duration: newProduct.duration } : {}),
      ...(newProduct.bonusMinutes !== undefined ? { bonus_minutes: newProduct.bonusMinutes } : {}),
      ...(newProduct.stampAmounts !== undefined ? { stamp_amounts: newProduct.stampAmounts } : {}),
    };

    setAdding(true);
    try {
      const { data, error } = await supabase.from("products").insert(dbProduct).select().single();
      if (error) throw error;
      setProducts((prev) => [...prev, { id: data.id, ...newProduct }]);
      toast.success('Produkt opprettet!', { duration: 2500, position: 'top-center' });
      closeAddModal();
    } catch (error) {
      console.error("Failed to create product:", error);
      toast.error(`Kunne ikke opprette produkt: ${error.message}`, { duration: 4000, position: 'top-center' });
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteProduct = async () => {
    if (!deleteConfirmProduct) return;
    setDeleting(true);
    try {
      const { error } = await supabase.from("products").delete().eq("id", deleteConfirmProduct.id);
      if (error) throw error;
      setProducts((prev) => prev.filter((p) => p.id !== deleteConfirmProduct.id));
      toast.success('Produkt slettet!', { duration: 2500, position: 'top-center' });
      setDeleteConfirmProduct(null);
    } catch (error) {
      console.error("Failed to delete product:", error);
      toast.error(`Kunne ikke slette produkt: ${error.message}`, { duration: 4000, position: 'top-center' });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <Toaster />
      <AdminDashboardNav />

      <div className="pm-container">
        {/* Header */}
        <div className="pm-header">
          <div>
            <h1><FiPackage style={{ marginRight: '12px', verticalAlign: 'middle' }} />Produktadministrasjon</h1>
            <p className="pm-subtitle">Se og administrer alle butikkprodukter</p>
          </div>
          <div className="pm-header-actions">
            <div className="pm-stat-pill">
              <FiPackage style={{ fontSize: '14px' }} />
              {products.length} Produkter
            </div>
            <div className="pm-stat-pill">
              <FiCheckCircle style={{ fontSize: '14px' }} />
              {activeCount} Aktive
            </div>
            <div className="pm-stat-pill">
              <FiXCircle style={{ fontSize: '14px' }} />
              {inactiveCount} Inaktive
            </div>
            <button className="btn btn-primary btn-sm" onClick={openAddModal}>
              <FiPlus size={14} /> Legg til produkt
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="pm-filters-section">
          <div className="pm-search-wrapper">
            <FiSearch className="pm-search-icon" />
            <input
              type="text"
              placeholder="Søk etter navn, beskrivelse eller ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pm-search-input"
            />
          </div>
          
          <div className="pm-filter-group">
            <select 
              value={selectedCategory} 
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="pm-filter-select"
            >
              <option value="">Alle kategorier</option>
              {allCategories.map(cat => (
                <option key={cat} value={cat}>
                  {cat.charAt(0).toUpperCase() + cat.slice(1)}
                </option>
              ))}
            </select>
            
            <select 
              value={statusFilter} 
              onChange={(e) => setStatusFilter(e.target.value)}
              className="pm-filter-select"
            >
              <option value="all">Alle statuser</option>
              <option value="active">Kun aktive</option>
              <option value="inactive">Kun inaktive</option>
            </select>
          </div>
        </div>

        {/* Products Table */}
        <div className="card pm-products-card">
          <div className="pm-products-header">
            <h2><FiPackage style={{ marginRight: '8px', verticalAlign: 'middle' }} />Produkter</h2>
            <span className="pm-products-count">{filteredProducts.length} produkter</span>
          </div>
          
          <div className="table-wrapper">
            {filteredProducts.length === 0 ? (
              <div className="pm-no-results">
                <FiPackage className="pm-no-results-icon" />
                <p>Ingen produkter funnet som samsvarer med kriteriene</p>
              </div>
            ) : (
              <table className="pm-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Produkt</th>
                  <th>Kategori</th>
                  <th>Pris</th>
                  <th>Type</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map(product => (
                  <tr key={product.id} className={product.active === false ? 'inactive-row' : ''}>
                    <td>
                      <span className={`pm-status-badge ${product.active === false ? 'inactive' : 'active'}`}>
                        {product.active === false ? (
                          <>
                            <FiXCircle /> Inaktiv
                          </>
                        ) : (
                          <>
                            <FiCheckCircle /> Aktiv
                          </>
                        )}
                      </span>
                    </td>
                    <td>
                      <div className="pm-product-cell">
                        <strong>{product.name}</strong>
                        {product.description && (
                          <span className="pm-product-desc">{product.description}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className="pm-category-badge">
                        {product.category || 'Other'}
                      </span>
                    </td>
                    <td className="pm-price-cell">
                      <strong>{product.price}</strong> kr
                    </td>
                    <td>{product.type || '—'}</td>
                    <td>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          onClick={() => openEditModal(product)}
                          className="pm-toggle-btn"
                          title="Rediger produkt"
                        >
                          <FiEdit2 /> Rediger
                        </button>
                        <button
                          onClick={() => toggleProductStatus(product.id, product.active)}
                          disabled={updatingProduct === product.id}
                          className={`pm-toggle-btn ${product.active === false ? 'inactive' : 'active'}`}
                          title={product.active === false ? 'Aktiver produkt' : 'Deaktiver produkt'}
                        >
                          {updatingProduct === product.id ? (
                            <span className="pm-btn-spinner"></span>
                          ) : product.active === false ? (
                            <>
                              <FiToggleLeft /> Aktiver
                            </>
                          ) : (
                            <>
                              <FiToggleRight /> Deaktiver
                            </>
                          )}
                        </button>
                        {product.active === false && (
                          <button
                            onClick={() => setDeleteConfirmProduct(product)}
                            className="pm-toggle-btn inactive"
                            title="Slett produkt"
                          >
                            <FiTrash2 /> Slett
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            )}
          </div>
        </div>
      </div>

      {editingProduct && (() => {
        // Only show fields this specific product actually uses, instead of every
        // ticket-shaped field on every product (e.g. no Varighet/Bonus on tilbehør).
        const hasDurationField = editingProduct.duration !== undefined;
        const hasBonusField = editingProduct.bonusMinutes !== undefined || editingProduct.bonusTime !== undefined;
        const hasStampsField = editingProduct.stampAmounts !== undefined || editingProduct.category === 'klippekort';
        return (
        <div className="modal-overlay" onClick={closeEditModal}>
          <div className="coupon-modal" onClick={(e) => e.stopPropagation()}>
            <div className="coupon-modal-header">
              <h2 style={{ margin: 0 }}><FiEdit2 style={{ marginRight: 8, verticalAlign: 'middle' }} />Rediger produkt</h2>
              <button className="coupon-modal-close" onClick={closeEditModal}>
                <FiX />
              </button>
            </div>

            <form onSubmit={handleEditSubmit} className="coupon-form" style={{ width: '100%' }}>
              <div className="form-group">
                <label htmlFor="edit-name">Navn</label>
                <input
                  id="edit-name"
                  name="name"
                  value={editForm.name}
                  onChange={handleEditFormChange}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="edit-description">Beskrivelse</label>
                <input
                  id="edit-description"
                  name="description"
                  value={editForm.description}
                  onChange={handleEditFormChange}
                  className="form-input"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="edit-price">Pris (kr)</label>
                  <input
                    id="edit-price"
                    name="price"
                    type="number"
                    min="0"
                    value={editForm.price}
                    onChange={handleEditFormChange}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="edit-category">Kategori</label>
                  <input
                    id="edit-category"
                    name="category"
                    value={editForm.category}
                    onChange={handleEditFormChange}
                    className="form-input"
                  />
                </div>
              </div>

              {(hasDurationField || hasBonusField) && (
                <div className="form-row">
                  {hasDurationField && (
                    <div className="form-group">
                      <label htmlFor="edit-duration">Varighet (min)</label>
                      <input
                        id="edit-duration"
                        name="duration"
                        type="number"
                        min="0"
                        placeholder="—"
                        value={editForm.duration}
                        onChange={handleEditFormChange}
                        className="form-input"
                      />
                    </div>
                  )}
                  {hasBonusField && (
                    <div className="form-group">
                      <label htmlFor="edit-bonusMinutes">Bonus (min)</label>
                      <input
                        id="edit-bonusMinutes"
                        name="bonusMinutes"
                        type="number"
                        min="0"
                        placeholder="—"
                        value={editForm.bonusMinutes}
                        onChange={handleEditFormChange}
                        className="form-input"
                      />
                    </div>
                  )}
                </div>
              )}

              {hasStampsField && (
                <div className="form-group">
                  <label htmlFor="edit-stampAmounts">Klipp (klippekort)</label>
                  <input
                    id="edit-stampAmounts"
                    name="stampAmounts"
                    type="number"
                    min="0"
                    placeholder="—"
                    value={editForm.stampAmounts}
                    onChange={handleEditFormChange}
                    className="form-input"
                  />
                </div>
              )}

              <div style={{ display: 'flex', gap: 10, width: '100%', marginTop: 8 }}>
                <button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={closeEditModal}>
                  Avbryt
                </button>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={saving}>
                  {saving ? 'Lagrer...' : 'Lagre endringer'}
                </button>
              </div>
            </form>
          </div>
        </div>
        );
      })()}

      {showAddModal && (
        <div className="modal-overlay" onClick={closeAddModal}>
          <div className="coupon-modal" onClick={(e) => e.stopPropagation()}>
            <div className="coupon-modal-header">
              <h2 style={{ margin: 0 }}><FiPlus style={{ marginRight: 8, verticalAlign: 'middle' }} />Legg til produkt</h2>
              <button className="coupon-modal-close" onClick={closeAddModal}>
                <FiX />
              </button>
            </div>

            <form onSubmit={handleAddSubmit} className="coupon-form" style={{ width: '100%' }}>
              <div className="form-group">
                <label htmlFor="add-name">Navn</label>
                <input
                  id="add-name"
                  name="name"
                  value={addForm.name}
                  onChange={handleAddFormChange}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="add-description">Beskrivelse</label>
                <input
                  id="add-description"
                  name="description"
                  value={addForm.description}
                  onChange={handleAddFormChange}
                  className="form-input"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="add-price">Pris (kr)</label>
                  <input
                    id="add-price"
                    name="price"
                    type="number"
                    min="0"
                    value={addForm.price}
                    onChange={handleAddFormChange}
                    className="form-input"
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="add-category">Kategori</label>
                  <input
                    id="add-category"
                    name="category"
                    placeholder="lek, golf, klippekort, tilbehør…"
                    value={addForm.category}
                    onChange={handleAddFormChange}
                    className="form-input"
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="add-duration">Varighet (min)</label>
                  <input
                    id="add-duration"
                    name="duration"
                    type="number"
                    min="0"
                    placeholder="Kun for tidsbaserte billetter"
                    value={addForm.duration}
                    onChange={handleAddFormChange}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="add-bonusMinutes">Bonus (min)</label>
                  <input
                    id="add-bonusMinutes"
                    name="bonusMinutes"
                    type="number"
                    min="0"
                    placeholder="Valgfritt"
                    value={addForm.bonusMinutes}
                    onChange={handleAddFormChange}
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="add-stampAmounts">Klipp (klippekort)</label>
                <input
                  id="add-stampAmounts"
                  name="stampAmounts"
                  type="number"
                  min="0"
                  placeholder="Kun for klippekort"
                  value={addForm.stampAmounts}
                  onChange={handleAddFormChange}
                  className="form-input"
                />
              </div>

              <div style={{ display: 'flex', gap: 10, width: '100%', marginTop: 8 }}>
                <button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={closeAddModal}>
                  Avbryt
                </button>
                <button type="submit" className="btn btn-primary" style={{ flex: 1 }} disabled={adding}>
                  {adding ? 'Oppretter...' : 'Opprett produkt'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {deleteConfirmProduct && (
        <div className="modal-overlay" onClick={() => setDeleteConfirmProduct(null)}>
          <div className="coupon-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420, textAlign: 'center' }}>
            <h2 style={{ margin: '0 0 8px' }}>Er du sikker?</h2>
            <p style={{ color: '#64748b', margin: '0 0 20px' }}>
              Dette sletter <strong>{deleteConfirmProduct.name}</strong> permanent. Handlingen kan ikke angres.
            </p>
            <div style={{ display: 'flex', gap: 10, width: '100%' }}>
              <button type="button" className="btn btn-secondary" style={{ flex: 1 }} onClick={() => setDeleteConfirmProduct(null)}>
                Avbryt
              </button>
              <button type="button" className="btn" style={{ flex: 1, background: '#dc2626', color: '#fff' }} onClick={handleDeleteProduct} disabled={deleting}>
                {deleting ? 'Sletter...' : 'Ja, slett'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
